import { Component, Injector, Input, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, Channel, ChannelService } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';

/**
 * Webchat channel configuration card (SPEC-2026-07-webchat-channel, T-20):
 * embed snippet (built from Channel.apis.uuid — RF-02) and the authorized-domains
 * allowlist (RF-05). Customization fields live in the channel edit form (RF-04).
 */
@Component({
  selector: 'channel-webchat-component',
  templateUrl: './channel-webchat.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChannelWebchatComponent extends AbstractComponent {

  @Input('channel') channel: Channel;

  newDomain: string = '';

  constructor(
    injector: Injector,
    public channelService: ChannelService,
  ) {
    super(injector);
  }

  get embedCode(): string {
    const uuid = this.channel?.apis?.uuid ?? '';
    return `<script async src="${environment.webchatWidgetUrl}/webchat.js" data-channel="${uuid}"></script>`;
  }

  get domains(): Array<string> {
    return this.channel?.metadata?.authorizedDomains ?? [];
  }

  copyEmbedCode(): void {
    navigator.clipboard.writeText(this.embedCode).then(() => {
      this.alertService.success(this.t('channel.channelWebchat.embedCopiedSuccess'));
    });
  }

  addDomain(): void {
    const domain = String(this.newDomain ?? '').trim().toLowerCase().replace(/^[a-z]+:\/\//, '').split('/')[0];
    if (!domain) return;
    const current = this.domains;
    if (current.includes(domain)) {
      this.newDomain = '';
      return;
    }
    this.saveDomains([...current, domain]);
    this.newDomain = '';
  }

  removeDomain(domain: string): void {
    this.saveDomains(this.domains.filter((item) => item !== domain));
  }

  private saveDomains(domains: Array<string>): void {
    this.loading = true;
    const updated = <Channel>{ ...this.channel, metadata: { ...(this.channel.metadata ?? {}), authorizedDomains: domains } };
    this.channelService.update(this.channel._id, updated)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channel: Channel) => {
          this.channel.metadata = { ...(this.channel.metadata ?? {}), authorizedDomains: domains };
          this.alertService.success(this.t('channel.channelWebchat.domainsSavedSuccess'));
        },
        error: (err) => this.alertService.error(err.error.message),
        complete: () => this.loading = false,
      });
  }
}
